import React, { useEffect, useState, useMemo } from 'react';
import {
  getStaffRevenue,
  getStaffUtilization
} from '../../services/analytics.service';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend
} from 'recharts';

/* ------------------------------------
   Spinner Overlay (Reusable)
------------------------------------ */
const ChartLoader = () => (
  <div className="absolute inset-0 flex items-center justify-center bg-white/70 z-10">
    <div className="h-10 w-10 rounded-full border-4 border-gray-200 border-t-blue-500 animate-spin" />
  </div>
);
const ChartEmptyState = ({ message }) => (
  <div className="absolute inset-0 flex items-center justify-center text-gray-500 z-10">
    <p className="text-sm text-center">{message}</p>
  </div>
);

const StaffDashboard = ({ filters }) => {
  const [revenueData, setRevenueData] = useState([]);
  const [utilizationData, setUtilizationData] = useState([]);
  const [loadingChart, setLoadingChart] = useState(false);
  const [loadingTable, setLoadingTable] = useState(false);

  /* ------------------------------------
     Fetch Data (Parallel)
  ------------------------------------ */
  useEffect(() => {
  if (!filters?.from || !filters?.to) return;

  const controller = new AbortController();

  const fetchData = async () => {
    try {
      setLoadingChart(true);
      setLoadingTable(true);

      const appliedFilters = {
        from: filters.from,
        to: filters.to
      };

      const [revenueRes, utilizationRes] = await Promise.all([
        getStaffRevenue(appliedFilters, controller.signal),
        getStaffUtilization(appliedFilters, controller.signal)
      ]);
      if (controller.signal.aborted) return;

      setRevenueData(revenueRes || []);
      setUtilizationData(utilizationRes || []);
    } catch (err) {
      if (err.name === 'AbortError') {
        console.log('StaffDashboard request aborted');
        return;
      }

      console.error('StaffDashboard load failed', err);
    } finally {
      if (!controller.signal.aborted) {
        setLoadingChart(false);
        setLoadingTable(false);
      }
    }
  };

  fetchData();

  return () => {
    controller.abort();
  };
}, [filters?.from, filters?.to]);

  /* ------------------------------------
     Memoized Table Data
  ------------------------------------ */
  const utilizationRows = useMemo(() => {
    return utilizationData.map(staff => ({
      name: staff.StaffName,
      booked: Number(staff.BookedHours || 0).toFixed(2),
      available: Number(staff.AvailableHours || 0).toFixed(2),
      utilization: Number(staff.UtilizationPercent || 0).toFixed(2)
    }));
  }, [utilizationData]);
const hasRevenueData = useMemo(() => {
  return revenueData.some(d => (d.Revenue || 0) > 0);
}, [revenueData]);

const hasUtilizationData = useMemo(() => {
  return utilizationRows.length > 0;
}, [utilizationRows]);

  return (
    <div className="staff-dashboard space-y-10">

      {/* ==============================
         Revenue by Staff (Chart)
      ============================== */}
     {/* Revenue by Staff */}
    <div className="card bg-white shadow-lg rounded-xl p-6 relative">
      <h2 className="text-xl font-semibold mb-4">🧑‍💼 Revenue by Staff</h2>

      <div className="relative" style={{ height: 300 }}>
        {loadingChart && <ChartLoader />}

        {!loadingChart && !hasRevenueData && (
          <ChartEmptyState message="No staff revenue data available for this period" />
        )}

        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={revenueData} barCategoryGap="20%">
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="StaffName" hide />
            <YAxis
              tickFormatter={(value) => {
                if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
                if (value >= 1_000) return `${(value / 1_000).toFixed(1)}k`;
                return value;
              }}
            />
            <Tooltip formatter={(v) => Number(v || 0).toLocaleString()} />
            <Legend />
            <Bar
              dataKey="Revenue"
              fill="#3b82f6"
              name="Revenue Generated"
              barSize={40}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>



      {/* ==============================
         Staff Utilization (Table)
      ============================== */}
      <div className="card bg-white shadow-lg rounded-xl p-6">
        <h2 className="text-xl font-semibold mb-4">⏱️ Staff Utilization</h2>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left">Staff Name</th>
                <th className="px-4 py-2 text-right">Booked Hours</th>
                <th className="px-4 py-2 text-right">Available Hours</th>
                <th className="px-4 py-2 text-right">Utilization %</th>
              </tr>
            </thead>

            <tbody className="bg-white divide-y divide-gray-200">
              {loadingTable ? (
                <tr>
                  <td colSpan={4} className="py-6 text-center text-gray-400">
                    Loading staff utilization…
                  </td>
                </tr>
              ) : !hasUtilizationData ? (
                <tr>
                  <td colSpan={4} className="py-10 text-center text-gray-500">
                    No staff utilization data found for this period
                  </td>
                </tr>
              ) : (
                utilizationRows.map((staff, idx) => (
                  <tr key={idx} className="hover:bg-gray-50">
                    <td className="px-4 py-2">{staff.name}</td>
                    <td className="px-4 py-2 text-right">{staff.booked}</td>
                    <td className="px-4 py-2 text-right">{staff.available}</td>
                    <td className="px-4 py-2 text-right">{staff.utilization}%</td>
                  </tr>
                ))
              )}
            </tbody>

          </table>
        </div>
      </div>
    </div>
  );
};

export default StaffDashboard;
